import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ErrorBoundary from "@/components/ErrorBoundary";
import PriorInteractions from "@/components/PriorInteractions";
import ReviewQueue from "@/components/ReviewQueue";
import SpinnerButton from "@/components/SpinnerButton";
import TeamLayout from "@/components/TeamLayout";
import WonDealFields from "@/components/WonDealFields";
import { teamApi } from "@/lib/api";
import { waLink } from "@/lib/phone";

const STATUSES = ["New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];
const STAFF_LEADS_PAGE_SIZE = 50;
const CLOSED_STATUSES = ["Closed Won", "Closed Lost"];
// Mirrors LOST_REASONS in backend/server.py, which validates on save.
const LOST_REASONS = ["Price", "Bought elsewhere", "Stock unavailable", "No response from customer", "Not a serious buyer", "Other"];

// Shared with AdminDashboard — same luxury chart palette.
const CHART_COLORS = ["#3D3D3D", "#B8860B", "#2F5E3D", "#7A2E2E", "#4A6FA5"];

function SummaryCard({ label, value, sublabel, testid }) {
  // Won-value card needs a fluid font-size — see `.scw-money-tile` in
  // index.css. The class wins over the inline 36px declaration via
  // !important so the value never wraps on a 375px viewport.
  const isMoney = typeof value === "string" && value.startsWith("MYR ");
  return (
    <div className="scw-card" style={{ padding: "24px 24px" }} data-testid={testid}>
      <p className="scw-eyebrow" style={{ marginBottom: 10 }}>{label}</p>
      <p
        className={isMoney ? "scw-money-tile" : undefined}
        style={{
          fontSize: isMoney ? 26 : 36,
          margin: 0,
          fontWeight: 500,
          whiteSpace: isMoney ? "nowrap" : undefined,
        }}
      >
        {value}
      </p>
      {sublabel && (
        <p style={{ fontSize: 12, margin: "6px 0 0 0", color: "var(--scw-muted)" }} data-testid={`${testid}-sublabel`}>
          {sublabel}
        </p>
      )}
    </div>
  );
}

function formatMyr(n) {
  // Salespeople care about the thousands, not the cents — and on a
  // 375px viewport, "MYR 119,750.50" is the only string that breaks
  // the tile out of its card. Drop cents unconditionally; the deal
  // count (sublabel) gives precision context if anyone needs it.
  const v = Math.round(Number(n) || 0);
  return `MYR ${v.toLocaleString("en-MY", { maximumFractionDigits: 0 })}`;
}

function EditPanel({ lead, adminEmail, onClose, onSave }) {
  // Closed leads open in read-only mode for team users — closed deals
  // have already fired their conversion value to Meta CAPI + Google
  // Ads, so reopening / re-editing must stay an admin-only action.
  // The team panel still wants the salesperson to be able to *review*
  // what they closed (status history, notes, won value/SKU).
  const readOnly = CLOSED_STATUSES.includes(lead.Status);

  const [status, setStatus] = useState(lead.Status);
  const [note, setNote] = useState("");
  const [apptDate, setApptDate] = useState(lead.Appointment_Date || "");
  const [apptTime, setApptTime] = useState(lead.Appointment_Time || "");
  const [wonValue, setWonValue] = useState(lead.Won_Value || "");
  const [wonSku, setWonSku] = useState(lead.Won_SKU || "");
  const [lostReason, setLostReason] = useState(lead.Lost_Reason || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr(""); setLoading(true);
    try {
      const body = { status, note };
      if (lead.Type === "Appointment") { body.appointment_date = apptDate; body.appointment_time = apptTime; }
      if (status === "Closed Won") { body.won_value = wonValue; body.won_sku = wonSku; }
      if (status === "Closed Lost") { body.lost_reason = lostReason; }
      const res = await teamApi.leadUpdate(lead.ID, body);
      onSave(res.data.lead);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end", zIndex: 50 }} onClick={onClose} data-testid="team-lead-drawer">
      <div style={{ width: "min(540px, 100%)", background: "#FFFFFF", height: "100%", overflowY: "auto", padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <p className="scw-eyebrow" data-testid="team-drawer-mode">
            Lead detail{readOnly ? " \u00b7 read-only" : ""}
          </p>
          <button onClick={onClose} className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} data-testid="team-drawer-close">Close</button>
        </div>
        <h2 style={{ fontSize: 24, margin: "0 0 18px 0" }}>{lead.Customer_Name}</h2>
        <PriorInteractions leadId={lead.ID} apiClient={teamApi} testIdPrefix="team-panel" />
        {readOnly && (
          <div
            style={{
              background: "#fff7e6",
              border: "1px solid #f0d9a1",
              padding: "10px 14px",
              fontSize: 12,
              color: "#7a5a13",
              marginBottom: 18,
            }}
            data-testid="team-drawer-readonly-banner"
          >
            This lead is closed. Review-only view for team {"\u2014"} reopening or
            editing a closed deal is admin-only (the conversion value has
            already been sent to Meta &amp; Google).
            {adminEmail && (
              <>
                {" "}
                Need to amend this deal?{" "}
                <a
                  href={`mailto:${adminEmail}?subject=${encodeURIComponent(
                    `Amend closed lead — ${lead.Customer_Name} (${lead.ID})`
                  )}`}
                  style={{ color: "#7a5a13", textDecoration: "underline" }}
                  data-testid="team-drawer-amend-mailto"
                >
                  Message {adminEmail}
                </a>
                .
              </>
            )}
          </div>
        )}
        <div style={{ borderTop: "1px solid var(--scw-line)", marginBottom: 24 }}>
          {[
            ["Type", lead.Type],
            ["Phone", lead.Customer_Phone
              ? (waLink(lead.Customer_Phone)
                ? <a href={waLink(lead.Customer_Phone)} target="_blank" rel="noreferrer" data-testid="team-drawer-wa-link">{lead.Customer_Phone}</a>
                : lead.Customer_Phone)
              : "\u2014"],
            ["Email", lead.Customer_Email || "\u2014"],
            ["Product", lead.Product_Watch || "\u2014"], ["Message", lead.Message || "\u2014"],
            ["Appointment", lead.Appointment_Date ? `${lead.Appointment_Date} ${lead.Appointment_Time}` : "\u2014"],
            ["Received", lead.Timestamp + " MYT"], ["Last update", lead.Last_Updated + " MYT"],
            // marketing_source is server-resolved (Pass 53) \u2014 see
            // _resolve_marketing_source() in backend/server.py.
            ["Marketing source", lead.marketing_source || "\u2014"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid var(--scw-line)" }}>
              <div style={{ width: 130, color: "var(--scw-muted)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>{k}</div>
              <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 18 }}>
          <label className="scw-label">Status</label>
          <select
            className="scw-select"
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              if (next === "Closed Won" && !wonSku && lead.Product_Watch) {
                setWonSku(lead.Product_Watch);
              }
            }}
            disabled={readOnly}
            data-testid="team-drawer-status"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <WonDealFields
          visible={status === "Closed Won"}
          wonValue={wonValue}
          wonSku={wonSku}
          onChangeValue={setWonValue}
          onChangeSku={setWonSku}
          readOnly={readOnly}
        />
        {/* Pass 56 — this drawer sets status too, so it needs the same
            required reason the Leads-tab drawer has. Without it a rep
            closing a lead lost from the dashboard would hit a 400 with
            no way to answer it. */}
        {status === "Closed Lost" && (
          <div style={{ marginBottom: 18 }}>
            <label className="scw-label">Reason lost</label>
            <select
              className="scw-select"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              disabled={readOnly}
              data-testid="team-drawer-lost-reason"
            >
              <option value="">Select a reason…</option>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
        {!readOnly && (
          <div style={{ marginBottom: 18 }}>
            <label className="scw-label">Append note</label>
            <textarea className="scw-textarea" value={note} onChange={(e) => setNote(e.target.value)} data-testid="team-drawer-note" />
          </div>
        )}
        {lead.Type === "Appointment" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
            <div>
              <label className="scw-label">Appointment date</label>
              <input className="scw-input" type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} disabled={readOnly} data-testid="team-drawer-apptdate" />
            </div>
            <div>
              <label className="scw-label">Appointment time</label>
              <input className="scw-input" type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} disabled={readOnly} data-testid="team-drawer-appttime" />
            </div>
          </div>
        )}
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        {!readOnly && (
          <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="team-drawer-save">Save</SpinnerButton>
        )}

        <div style={{ marginTop: 32 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Notes</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Notes || "\u2014"}</pre>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Status history</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Status_History || "\u2014"}</pre>
        </div>
      </div>
    </div>
  );
}

export default function TeamPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [auth, setAuth] = useState({ loading: true, ok: false });
  const [active, setActive] = useState(null);
  // Frontend pagination — backend returns the full list, we slice for
  // display. Page resets to 1 if the underlying list shrinks (e.g. a
  // lead moves from open → closed via the drawer edit).
  const [openPage, setOpenPage] = useState(1);
  const [closedPage, setClosedPage] = useState(1);

  useEffect(() => {
    teamApi.me().then(() => setAuth({ loading: false, ok: true }))
      .catch(() => setAuth({ loading: false, ok: false }));
  }, []);

  useEffect(() => {
    if (auth.ok) teamApi.dashboard().then((r) => setData(r.data));
  }, [auth.ok]);

  if (auth.loading) return <div style={{ padding: 40 }}>{"Loading\u2026"}</div>;
  if (!auth.ok) return <Navigate to="/team/login" replace />;

  if (!data) return (<TeamLayout pageTitle="Team Panel — SCW Contact Hub"><p>{"Loading\u2026"}</p></TeamLayout>);

  // Pagination math — derive page slices + total pages from the full lists.
  const openTotalPages = Math.max(1, Math.ceil(data.open_leads.length / STAFF_LEADS_PAGE_SIZE));
  const closedTotalPages = Math.max(1, Math.ceil(data.closed_leads_30d.length / STAFF_LEADS_PAGE_SIZE));
  const openSafePage = Math.min(openPage, openTotalPages);
  const closedSafePage = Math.min(closedPage, closedTotalPages);
  const openSlice = data.open_leads.slice(
    (openSafePage - 1) * STAFF_LEADS_PAGE_SIZE,
    openSafePage * STAFF_LEADS_PAGE_SIZE,
  );
  const closedSlice = data.closed_leads_30d.slice(
    (closedSafePage - 1) * STAFF_LEADS_PAGE_SIZE,
    closedSafePage * STAFF_LEADS_PAGE_SIZE,
  );

  return (
    <TeamLayout pageTitle="Team Panel — SCW Contact Hub">
      <div data-testid="team-panel">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Your view</p>
        <h1 style={{ fontSize: 28, margin: "0 0 24px 0" }}>Hello, {data.me?.name}.</h1>

        {data.summary && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 32 }}>
            <SummaryCard label="My leads today" value={data.summary.today} testid="team-summary-today" />
            <SummaryCard label="My leads this week" value={data.summary.week} testid="team-summary-week" />
            <SummaryCard label="My leads this month" value={data.summary.month} testid="team-summary-month" />
            <SummaryCard label="My appointments (next 7d)" value={data.summary.appointments_upcoming} testid="team-summary-appts" />
            <SummaryCard
              label="My closed-won this month"
              value={formatMyr(data.summary.won_value_month)}
              sublabel={`across ${data.summary.won_count_month} deal${data.summary.won_count_month === 1 ? "" : "s"}`}
              testid="team-summary-won"
            />
          </div>
        )}

        {/* Pass 46 — Review-request queue moved BELOW the
            "Appointments today" block. See render block further
            down. */}

        {Array.isArray(data.duty_week) && data.duty_week.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 32 }} data-testid="team-duty-week">
            {data.duty_week.map((d) => {
              const isClosed = d.is_off || d.is_sunday;
              const closedLabel = d.is_off ? (d.off_reason || "Closed") : (d.is_sunday ? "Closed" : "");
              const personLabel = !d.is_sunday && d.person_name ? d.person_name : "";
              const mine = personLabel && personLabel === data.me?.name;
              return (
                <div
                  key={d.date}
                  className="scw-card"
                  style={{
                    padding: 14,
                    background: isClosed ? "#f4f4f4" : "#FFFFFF",
                    borderTop: d.is_today ? "3px solid #5C5C5C" : "1px solid var(--scw-line)",
                    minHeight: 110,
                  }}
                  data-testid={`team-duty-${d.date}`}
                >
                  <p className="scw-eyebrow" style={{ marginBottom: 4 }}>
                    {d.weekday}{d.is_today ? " \u00b7 today" : ""}
                  </p>
                  <p style={{ fontSize: 13, color: "var(--scw-muted)", margin: "0 0 10px 0" }}>{d.date}</p>
                  {closedLabel && (
                    <p style={{ fontSize: 13, margin: "0 0 6px 0", color: "var(--scw-muted)", fontStyle: "italic" }}>{closedLabel}</p>
                  )}
                  {personLabel ? (
                    <p style={{ fontSize: 14, margin: 0, color: mine ? "#3D3D3D" : "#5C5C5C", fontWeight: mine ? 600 : 500 }}>
                      {personLabel}{mine ? " (you)" : ""}
                    </p>
                  ) : (!isClosed && (
                    <p style={{ fontSize: 14, margin: 0, color: "var(--scw-muted)", fontStyle: "italic" }}>Unassigned</p>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {data.month_by_type && (
          <>
            <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>This month at a glance (my leads)</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 32 }}>
              <ErrorBoundary label="Leads by type chart">
                <div className="scw-card" data-testid="team-chart-type">
                  <p className="scw-eyebrow" style={{ marginBottom: 12 }}>My leads by type (MTD)</p>
                  {(data.month_by_type || []).every((d) => d.value === 0) ? (
                    <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
                  ) : (
                    <div style={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.month_by_type || []}>
                          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9a9a9a" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                          <Tooltip cursor={{ fill: "rgba(92,92,92,0.06)" }} />
                          <Bar dataKey="value">
                            {(data.month_by_type || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </ErrorBoundary>
              <ErrorBoundary label="Leads by status chart">
                <div className="scw-card" data-testid="team-chart-status">
                  <p className="scw-eyebrow" style={{ marginBottom: 12 }}>My leads by status (MTD)</p>
                  {(data.month_by_status || []).every((d) => d.value === 0) ? (
                    <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
                  ) : (
                    <div style={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={data.month_by_status} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                            {(data.month_by_status || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </ErrorBoundary>
              <ErrorBoundary label="Leads by source chart">
                <div className="scw-card" data-testid="team-chart-source">
                  <p className="scw-eyebrow" style={{ marginBottom: 12 }}>My leads by source (MTD)</p>
                  {!(data.month_by_source || []).length ? (
                    <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
                  ) : (
                    <div style={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={data.month_by_source} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                            {(data.month_by_source || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </ErrorBoundary>
            </div>
          </>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 32 }}>
          <div className="scw-card" data-testid="team-duty-today">
            <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Today</p>
            <p style={{ fontSize: 18, margin: 0 }}>
              {data.is_on_duty_today
                ? "You are on duty today."
                : (data.duty_today ? `On duty today: ${data.duty_today.Person_Name}` : "On duty today: not yet assigned")}
            </p>
          </div>
          <div className="scw-card" data-testid="team-duty-tomorrow">
            <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Tomorrow</p>
            <p style={{ fontSize: 18, margin: 0 }}>
              {data.duty_tomorrow ? `On duty tomorrow: ${data.duty_tomorrow.Person_Name}` : "On duty tomorrow: not yet assigned"}
            </p>
          </div>
        </div>

        <h2 style={{ fontSize: 20, margin: "32px 0 14px 0" }}>Appointments today</h2>
        <div className="scw-card" style={{ padding: 0 }} data-testid="team-appts-today">
          {!data.appointments_today || data.appointments_today.length === 0 ? (
            <p style={{ padding: 24, color: "var(--scw-muted)" }} data-testid="team-appts-today-empty">No appointments scheduled for today.</p>
          ) : (
            <div className="scw-table-wrap">
              <table className="scw-table">
                <thead><tr><th>Time</th><th>Customer</th><th>Watch</th><th>Specialist</th><th>Status</th></tr></thead>
                <tbody>
                  {data.appointments_today.map((r) => {
                    const mine = r.Duty_Person && data.me?.name && r.Duty_Person === data.me.name;
                    return (
                      <tr key={r.ID}
                          style={{ borderLeft: mine ? "3px solid #5C5C5C" : "3px solid transparent" }}
                          data-testid={`team-appt-today-${r.ID}`}>
                        <td style={{ fontWeight: mine ? 600 : 400 }}>{r.Appointment_Time || "\u2014"}</td>
                        <td>{r.Customer_Name}</td>
                        <td>{r.Product_Watch || "\u2014"}</td>
                        <td>{r.Duty_Person || "\u2014"}</td>
                        <td><span className="scw-pill">{r.Status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pass 46 — Review-request queue placed BELOW "Appointments
            today". Hidden when empty. Hard-scoped server-side to
            Duty_Person == me. Each row deep-links into
            /team/leads?open=<id> so the send composer renders inside
            the lead drawer (the only place the salesperson actually
            sends). */}
        <ReviewQueue apiClient={teamApi} leadsPath="/team/leads" title="Review requests pending" />

        {/* Pass 56 — the rep's own SLA queue. The daily summary has
            always flagged breaches, but only to the admin, so the
            person who could actually fix a late lead was the one who
            never saw it. Same rules the summary and Analytics read. */}
        {(data.sla_queue || []).length > 0 && (
          <>
            <h2 style={{ fontSize: 20, margin: "32px 0 6px 0" }} data-testid="team-sla-heading">
              Needs attention ({data.sla_queue.length})
            </h2>
            <p style={{ fontSize: 12, color: "var(--scw-muted)", marginBottom: 14, lineHeight: 1.6 }}>
              First contact is due by the end of the next opening day. A lead quiet for{" "}
              {data.sla_rules?.quiet_days ?? 21} days needs a decision from you: record the next step, or close it lost with a reason.
            </p>
            <div className="scw-card" style={{ padding: 0, marginBottom: 8 }} data-testid="team-sla-queue">
              <div className="scw-table-wrap">
                <table className="scw-table">
                  <thead><tr>
                    <th>Customer</th><th>Watch</th><th>Status</th><th>Last touched</th><th>Flag</th>
                  </tr></thead>
                  <tbody>
                    {data.sla_queue.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => navigate(`/team/leads?open=${encodeURIComponent(r.id)}`)}
                        style={{ cursor: "pointer" }}
                        data-testid={`team-sla-row-${r.id}`}
                      >
                        <td>{r.customer_name}</td>
                        <td>{r.product || "\u2014"}</td>
                        <td><span className="scw-pill">{r.status}</span></td>
                        <td>{(r.last_updated || "").split(" ")[0]}</td>
                        <td>
                          {r.breaches.map((b) => (
                            <span
                              key={b}
                              className="scw-pill"
                              style={{ background: "#fff0d4", color: "#7a5a13", borderColor: "#f0d9a1", marginRight: 4 }}
                            >
                              {(data.sla_rules?.labels || {})[b] || b}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <h2 style={{ fontSize: 20, margin: "32px 0 14px 0" }}>My open leads ({data.open_leads.length})</h2>
        <div className="scw-card" style={{ padding: 0 }} data-testid="team-open-leads">
          {data.open_leads.length === 0 ? (
            <p style={{ padding: 24, color: "var(--scw-muted)" }} data-testid="team-open-empty">No open leads assigned to you right now.</p>
          ) : (
            <div className="scw-table-wrap">
              <table className="scw-table">
                <thead><tr><th>Timestamp</th><th>Type</th><th>Customer</th><th>Product</th><th>Status</th><th>Last update</th></tr></thead>
                <tbody>
                  {openSlice.map((r) => (
                    <tr key={r.ID} onClick={() => setActive(r)} style={{ cursor: "pointer" }} data-testid={`team-open-row-${r.ID}`}>
                      <td>{r.Timestamp}</td>
                      <td>{r.Type}</td>
                      <td>{r.Customer_Name}</td>
                      <td>{r.Product_Watch || "\u2014"}</td>
                      <td><span className="scw-pill">{r.Status}</span></td>
                      <td>{r.Last_Updated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {data.open_leads.length > STAFF_LEADS_PAGE_SIZE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }} data-testid="team-open-pagination">
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    disabled={openSafePage <= 1} onClick={() => setOpenPage((p) => Math.max(1, p - 1))}
                    data-testid="team-open-prev-btn">{'\u2190'} Previous</button>
            <span className="scw-eyebrow" data-testid="team-open-page-label">Page {openSafePage} of {openTotalPages}</span>
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    disabled={openSafePage >= openTotalPages} onClick={() => setOpenPage((p) => Math.min(openTotalPages, p + 1))}
                    data-testid="team-open-next-btn">Next {'\u2192'}</button>
          </div>
        )}

        <h2 style={{ fontSize: 20, margin: "32px 0 14px 0" }}>My closed leads (last 30 days)</h2>
        <div className="scw-card" style={{ padding: 0 }} data-testid="team-closed-leads">
          {data.closed_leads_30d.length === 0 ? (
            <p style={{ padding: 24, color: "var(--scw-muted)" }}>No closed leads in the last 30 days.</p>
          ) : (
            <div className="scw-table-wrap">
              <table className="scw-table">
                <thead><tr><th>Timestamp</th><th>Type</th><th>Customer</th><th>Product</th><th>Status</th></tr></thead>
                <tbody>
                  {closedSlice.map((r) => (
                    <tr key={r.ID} onClick={() => setActive(r)} style={{ cursor: "pointer" }} data-testid={`team-closed-row-${r.ID}`}>
                      <td>{r.Timestamp}</td>
                      <td>{r.Type}</td>
                      <td>{r.Customer_Name}</td>
                      <td>{r.Product_Watch || "\u2014"}</td>
                      <td><span className="scw-pill">{r.Status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {data.closed_leads_30d.length > STAFF_LEADS_PAGE_SIZE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }} data-testid="team-closed-pagination">
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    disabled={closedSafePage <= 1} onClick={() => setClosedPage((p) => Math.max(1, p - 1))}
                    data-testid="team-closed-prev-btn">{'\u2190'} Previous</button>
            <span className="scw-eyebrow" data-testid="team-closed-page-label">Page {closedSafePage} of {closedTotalPages}</span>
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    disabled={closedSafePage >= closedTotalPages} onClick={() => setClosedPage((p) => Math.min(closedTotalPages, p + 1))}
                    data-testid="team-closed-next-btn">Next {'\u2192'}</button>
          </div>
        )}
      {active && (
        <EditPanel lead={active} adminEmail={data.admin_email} onClose={() => setActive(null)}
          onSave={(updated) => {
            setData((cur) => ({
              ...cur,
              open_leads: cur.open_leads
                .map((r) => r.ID === updated.ID ? updated : r)
                .filter((r) => updated.ID !== r.ID || ["New", "Contacted", "In Progress"].includes(r.Status)),
              closed_leads_30d: ["Closed Won", "Closed Lost"].includes(updated.Status)
                ? [updated, ...cur.closed_leads_30d.filter((r) => r.ID !== updated.ID)]
                : cur.closed_leads_30d,
            }));
            setActive(updated);
          }} />
      )}
      </div>
    </TeamLayout>
  );
}
